import React, { useState, useEffect, useRef } from "react";
import "./Chatbot.css";

const FLOW = {
  START: {
    message: "Hi I'm Amy, assistant for Amy Floris.\nWhat is your business type?",
    options: [
      { label: "Manufacturer", next: "MFG_PRODUCT" },
      { label: "Trader", next: "TRD_ASSEMBLE" },
    ],
  },
  MFG_PRODUCT: {
    message: "What product do you manufacture?",
    input: true,
    next: "MFG_BIS",
  },
  MFG_BIS: {
    message: "Is the product BIS mandatory?",
    options: [
      { label: "Yes", next: "MFG_BIS_YES" },
      { label: "No", next: "MFG_BIS_NO" },
    ],
  },
  MFG_BIS_YES: {
    message: "BIS Certification Flow: We'd love to share the BIS roadmap with you.\n\nPlease give us your Name, Email, and Phone Number, and we'll share the roadmap.",
    form: true,
    next: "SUCCESS_END",
  },
  MFG_BIS_NO: {
    message: "Optional BIS Support: We can help with BIS procurement/certification.\n\nPlease give us your Name, Email, and Phone Number, and our team will support you.",
    form: true,
    next: "SUCCESS_END",
  },
  TRD_ASSEMBLE: {
    message:
      "Do you plan to buy spare parts/raw materials externally and assemble them yourself?",
    options: [
      { label: "Yes", next: "TRD_SOURCE" },
      { label: "No", next: "TRD_FINISHED" },
    ],
  },
  TRD_SOURCE: {
    message: "Where will you source the parts/raw materials from?",
    options: [
      { label: "India", next: "TRD_SOURCE_INDIA" },
      { label: "Other country", next: "TRD_SOURCE_OTHER" },
    ],
  },
  TRD_SOURCE_INDIA: {
    message: "Eligible for BIS: We can proceed with the BIS assessment.\n\nPlease give us your Name, Email, and Phone Number, and we'll proceed.",
    form: true,
    next: "SUCCESS_END",
  },
  TRD_SOURCE_OTHER: {
    message: "Which country?",
    input: true,
    processInput: (val) => {
      if (val.toLowerCase().includes("china")) {
        return "TRD_SOURCE_CHINA";
      }
      return "TRD_SOURCE_ANY_OTHER";
    },
  },
  TRD_SOURCE_CHINA: {
    message: "BIS certification is not applicable / cannot proceed under this route.",
    end: true,
  },
  TRD_SOURCE_ANY_OTHER: {
    message: "BIS certification can be explored: We can proceed with the assessment.\n\nPlease give us your Name, Email, and Phone Number, and we'll get started.",
    form: true,
    next: "SUCCESS_END",
  },
  TRD_FINISHED: {
    message: "Are you procuring the finished product?",
    options: [
      { label: "Yes", next: "TRD_FINISHED_YES" },
      { label: "No", next: "TRD_FINISHED_NO" },
    ],
  },
  TRD_FINISHED_YES: {
    message: "Please provide product details:",
    input: true,
    next: "TRD_FINISHED_DETAILS",
  },
  TRD_FINISHED_DETAILS: {
    message: "We will check the BIS requirement for your product.\n\nPlease give us your Name, Email, and Phone Number, and we'll share the details.",
    form: true,
    next: "SUCCESS_END",
  },
  TRD_FINISHED_NO: {
    message: "We need to clarify your procurement/manufacturing model.\n\nPlease give us your Name, Email, and Phone Number, and our experts will reach out to you.",
    form: true,
    next: "SUCCESS_END",
  },
  SUCCESS_END: {
    message: "Thank you! Your details have been submitted successfully. A member of our team will contact you shortly with the requested information. Have a great day!",
    end: true,
  }
};

function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentStepId, setCurrentStepId] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [isTyping, setIsTyping] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const chatBodyRef = useRef(null);

  useEffect(() => {
    const chatBody = chatBodyRef.current;
    if (!chatBody) return;

    // Wait slightly to ensure any CSS transitions or paints for options/forms are completed
    setTimeout(() => {
      const typingIndicator = chatBody.querySelector(".typing-indicator");
      
      // If the bot is typing, we just smoothly scroll to bottom to show the dots
      if (typingIndicator) {
        chatBody.scrollTo({
          top: chatBody.scrollHeight,
          behavior: "smooth",
        });
        return;
      }

      const latestBotMsgs = chatBody.querySelectorAll(".bot-message:not(.typing-indicator)");
      const lastMsgNode = latestBotMsgs[latestBotMsgs.length - 1];

      if (lastMsgNode) {
        const msgTop = lastMsgNode.offsetTop;
        const chatHeight = chatBody.clientHeight;
        const scrollHeight = chatBody.scrollHeight;

        // If the new content (msg + form/options) is tall, don't just jump to the bottom.
        // Anchor the scroll slightly higher so the user retains conversational context (the "past msg").
        const contentHeightBelowMsg = scrollHeight - msgTop;

        if (contentHeightBelowMsg > chatHeight - 100) { 
          const prevSibling = lastMsgNode.previousElementSibling;
          let target = msgTop - 60; // fallback if no prev sibling
          
          if (prevSibling) {
             // Anchor to the element right before the new bot message (usually the user's reply)
             target = prevSibling.offsetTop - 15;
          }
          
          // Clamp target to >= 0
          target = Math.max(0, target);

          chatBody.scrollTo({
            top: target, // 20px padding from the header
            behavior: "smooth",
          });
        } else {
          // Everything fits on screen easily, just scroll to the very bottom
          chatBody.scrollTo({
            top: scrollHeight,
            behavior: "smooth",
          });
        }
      }
    }, 50);
  }, [messages, currentStepId, isTyping]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      startFlow();
    }
  }, [isOpen]);

  const startFlow = () => {
    setMessages([]);
    setFormData({ name: '', email: '', phone: '' });
    advanceToStep("START");
  };

  const advanceToStep = (stepId) => {
    const step = FLOW[stepId];
    if (!step) return;

    setCurrentStepId(stepId);
    
    // Simulate typing delay for a more natural feel
    setIsTyping(true);
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          sender: "bot",
          text: step.message,
        },
      ]);
      setIsTyping(false);
    }, 600);
  };

  const handleOptionClick = (option) => {
    // Add user's selection to messages
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        sender: "user",
        text: option.label,
      },
    ]);
    
    setCurrentStepId(null); // Temporarily disable inputs while processing
    advanceToStep(option.next);
  };

  const handleSendInput = () => {
    const text = inputValue.trim();
    if (!text) return;

    // Add user's text to messages
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        sender: "user",
        text: text,
      },
    ]);

    setInputValue("");
    setCurrentStepId(null);

    const step = FLOW[currentStepId];
    if (step.processInput) {
      const nextStepId = step.processInput(text);
      advanceToStep(nextStepId);
    } else if (step.next) {
      advanceToStep(step.next);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSendInput();
    }
  };

  const handleFormSubmit = async () => {
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      alert("Please check that all fields are filled.");
      return;
    }

    setIsSubmitting(true);

    // Compile chat history to send as the "message" field
    const conversation = messages
      .map((m) => `${m.sender === "bot" ? "Amy (Bot)" : "User"}: ${m.text.replace(/\n/g, ' ')}`)
      .join("\n");
      
    const payloadMessage = `[Amy Chatbot Lead]\n\n--- Conversation Transcript ---\n${conversation}`;

    try {
      const response = await fetch("https://contact-api-coral.vercel.app/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: payloadMessage,
        }),
      });

      if (response.ok) {
        // Add user's form submission to messages list for context visually
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            sender: "user",
            text: `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}`,
          },
        ]);

        const nextStep = FLOW[currentStepId].next;
        setCurrentStepId(null);
        if (nextStep) {
          advanceToStep(nextStep);
        }
      } else {
        alert("Failed to submit details. Please try again or use our Contact page.");
      }
    } catch (error) {
      alert("Network error. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderBubbleText = (text) => {
    return text.split('\n').map((line, i) => (
      <span key={i}>
        {line}
        {i !== text.split('\n').length - 1 && <br />}
      </span>
    ));
  };

  const currentStep = currentStepId ? FLOW[currentStepId] : null;

  return (
    <>
      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <div className="chatbot-avatar">
                {/* Fallback structure kept just in case image doesn't load */}
                <img src="/favicon.ico" alt="Amy" style={{ background: 'white' }} onError={(e) => e.target.style.display = 'none'} />
                <div className="avatar-fallback">A</div>
              </div>
              <div>
                <strong>Amy</strong>
                <span>Online</span>
              </div>
            </div>

            <button
              className="chatbot-close"
              onClick={() => setIsOpen(false)}
            >
              ×
            </button>
          </div>

          <div className="chatbot-body" ref={chatBodyRef}>
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={
                  msg.sender === "user" ? "user-message" : "bot-message"
                }
              >
                {renderBubbleText(msg.text)}
              </div>
            ))}
            
            {isTyping && (
              <div className="bot-message typing-indicator">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
            )}
            
            {currentStep && !isTyping && currentStep.options && (
              <div className="chatbot-options fade-in">
                {currentStep.options.map((opt, idx) => (
                  <button
                    key={idx}
                    className="chatbot-option-btn"
                    onClick={() => handleOptionClick(opt)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {currentStep && !isTyping && currentStep.form && (
              <div className="chatbot-form fade-in">
                <input 
                  type="text" 
                  placeholder="Name" 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
                <input 
                  type="email" 
                  placeholder="Email" 
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
                <input 
                  type="tel" 
                  placeholder="Phone Number" 
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
                <button 
                  className={`chatbot-submit-btn ${isSubmitting ? 'disabled' : ''}`}
                  onClick={handleFormSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit Details"}
                </button>
              </div>
            )}
            
            {currentStep && !isTyping && currentStep.end && (
               <div className="chatbot-options fade-in">
                 <button
                   className="chatbot-restart-btn"
                   onClick={startFlow}
                 >
                   ↻ Start Over
                 </button>
               </div>
            )}
            
            {/* smart scroll replaces bottom anchor div */}
          </div>

          <div className="chatbot-input">
            <input
              type="text"
              placeholder={
                currentStep && currentStep.input && !isTyping
                  ? "Type your answer..."
                  : "Please select an option above"
              }
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={!currentStep || !currentStep.input || isTyping}
            />

            <button 
              onClick={handleSendInput}
              disabled={!currentStep || !currentStep.input || !inputValue.trim() || isTyping}
              className={(!currentStep || !currentStep.input || !inputValue.trim() || isTyping) ? "disabled" : ""}
            >
              <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M48 448l416-192L48 64v149.333L346 256 48 298.667z"></path></svg>
            </button>
          </div>
        </div>
      )}

      <button
        className={`chatbot-button ${isOpen ? "active" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open chat"
      >
        {isOpen ? <span style={{ transform: "translateY(-1px)", display: "inline-block" }}>×</span> : "💬"}
      </button>
    </>
  );
}

export default Chatbot;
